import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import LoadingSpinner from '../../../components/LoadingSpinner';
import {
  ArrowLeft,
  User,
  Envelope,
  Phone,
  MapPin,
  CalendarBlank,
  Users,
  TrendUp,
  CheckCircle,
  XCircle,
  Bell,
  UserCircle as UserCircleIcon
} from '@phosphor-icons/react';

export default function GroupLeaderMemberDetail() {
  const navigate = useNavigate();
  const { memberId } = useParams();
  const [loading, setLoading] = useState(true);
  const [memberData, setMemberData] = useState(null);
  const [membershipData, setMembershipData] = useState(null);
  const [stats, setStats] = useState({ rate: 0, attended: 0, total: 0, confirmed: 0 });

  useEffect(() => {
    loadMemberData();
  }, [memberId]);

  const loadMemberData = async () => {
    try {
      setLoading(true);

      // Get member user data
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', memberId)
        .single();

      if (!userData) {
        alert('Member not found');
        navigate('/group-leader/members');
        return;
      }

      setMemberData(userData);

      // Get member's group membership
      const { data: membership } = await supabase
        .from('group_members')
        .select('id, group_id, groups(id, name, depth, parent_id)')
        .eq('user_id', memberId)
        .single();

      setMembershipData(membership);

      // Get attendance stats
      const { data: attendanceRecords } = await supabase
        .from('attendance_records')
        .select('*')
        .eq('user_id', memberId);

      const { data: confirmations } = await supabase
        .from('event_confirmations')
        .select('*')
        .eq('user_id', memberId)
        .eq('status', 'confirmed');

      const { data: allEvents } = await supabase
        .from('events')
        .select('id')
        .eq('org_id', userData.org_id)
        .lte('start_date', new Date().toISOString().split('T')[0]);

      const attended = attendanceRecords?.length || 0;
      const confirmed = confirmations?.length || 0;
      const total = allEvents?.length || 0;
      const rate = total > 0 ? Math.round((attended / total) * 100) : 0;

      setStats({ rate, attended, total, confirmed });

    } catch (error) {
      console.error('Error loading member data:', error);
      alert('Failed to load member data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return <LoadingSpinner message="Loading member details..." />;
  }

  if (!memberData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* Header */}
      <div className="bg-white px-6 pt-6 pb-16 safe-area-top border-b border-gray-200">
        <button
          onClick={() => navigate('/group-leader/members')}
          className="flex items-center gap-2 mb-6 hover:opacity-60 transition-opacity"
        >
          <ArrowLeft size={24} weight="light" className="text-gray-900" />
          <span className="font-medium text-gray-900">Back to Members</span>
        </button>

        {/* Profile Picture */}
        <div className="relative w-32 h-32 mx-auto mb-6">
          {memberData?.avatar_url ? (
            <img
              src={memberData.avatar_url}
              alt={memberData.name}
              className="w-32 h-32 rounded-3xl object-cover border-4 border-gray-200"
            />
          ) : (
            <div className="w-32 h-32 rounded-3xl bg-gray-100 flex items-center justify-center border-4 border-gray-200">
              <User size={64} weight="light" className="text-gray-400" />
            </div>
          )}
        </div>

        {/* Name and Role */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{memberData?.name}</h1>
          {membershipData && (
            <div className="flex items-center justify-center gap-2">
              <span className={`px-3 py-1 ${
                memberData.leadership_level === 'subgroup' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
              } text-sm font-medium rounded-lg`}>
                {memberData.leadership_level === 'subgroup' ? 'Subgroup Leader' : 'Member'}
              </span>
              {membershipData.groups && (
                <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg">
                  {membershipData.groups.name}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-4">
        {/* Stats Card */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-bold text-gray-900 text-lg">Attendance Stats</h2>
          </div>

          <div className="px-6 py-4">
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Attendance Rate</span>
                <span className="text-2xl font-bold text-blue-600">{stats.rate}%</span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${stats.rate}%` }}
                ></div>
              </div>
              
              <p className="text-xs text-gray-500 mt-2">
                {stats.attended} out of {stats.total} events attended
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <CheckCircle size={20} weight="fill" className="text-green-600" />
                  <span className="text-2xl font-bold text-gray-900">{stats.attended}</span>
                </div>
                <p className="text-xs text-gray-600">Attended</p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <CalendarBlank size={20} weight="light" className="text-blue-600" />
                  <span className="text-2xl font-bold text-gray-900">{stats.confirmed}</span>
                </div>
                <p className="text-xs text-gray-600">Confirmed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information Card */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-bold text-gray-900 text-lg">Contact Information</h2>
          </div>

          <div className="divide-y divide-gray-100">
            <div className="flex items-center gap-4 px-6 py-4">
              <Envelope size={24} weight="light" className="text-gray-600" />
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Email</p>
                <p className="text-gray-900">{memberData?.email}</p>
              </div>
            </div>

            {memberData?.phone && (
              <div className="flex items-center gap-4 px-6 py-4">
                <Phone size={24} weight="light" className="text-gray-600" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">Phone</p>
                  <p className="text-gray-900">{memberData.phone}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Personal Information Card */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-bold text-gray-900 text-lg">Personal Information</h2>
          </div>

          <div className="divide-y divide-gray-100">
            {memberData?.date_of_birth && (
              <div className="flex items-center gap-4 px-6 py-4">
                <CalendarBlank size={24} weight="light" className="text-gray-600" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">Date of Birth</p>
                  <p className="text-gray-900">{formatDate(memberData.date_of_birth)}</p>
                </div>
              </div>
            )}

            {memberData?.address && (
              <div className="flex items-start gap-4 px-6 py-4">
                <MapPin size={24} weight="light" className="text-gray-600 mt-1" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">Address</p>
                  <p className="text-gray-900">{memberData.address}</p>
                  {(memberData.city || memberData.state || memberData.post_code) && (
                    <p className="text-gray-900 mt-1">
                      {[memberData.city, memberData.state, memberData.post_code]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                  {memberData.country && (
                    <p className="text-gray-900">{memberData.country}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}